// AGC: useVoiceCalibration — State machine hook for voice calibration wizard
// AGC: Manages phrase queue, recording, WER results, quality gates, and profile save.
// AGC: Uses useVoiceRecording for actual audio capture (reuses MediaRecorder config).
// AGC: Noise baseline analyzed client-side via Web Audio API AnalyserNode.

import { useState, useRef, useCallback, useEffect } from 'react'
import { useAuth } from './useAuth'
import { useTranslation } from './useTranslation'
import { getBackendUrl } from '../utils/backendUrl'

// ─────────────────────────────────────────────────────────
// AGC: Types
// ─────────────────────────────────────────────────────────

export type CalibrationState =
  | 'idle'
  | 'loading_phrases'
  | 'recording_noise'
  | 'recording_phrase'
  | 'processing_phrase'
  | 'phrase_result'
  | 'complete'
  | 'saving'
  | 'saved'
  | 'error'

export interface WERMetrics {
  wer: number
  insertions: number
  deletions: number
  substitutions: number
  totalWords: number
}

export interface SlotAccuracy {
  slotType: string
  expected: string
  transcribed: string
  correct: boolean
}

export interface CalibrationPhraseResult {
  phraseIndex: number
  expectedText: string
  transcribedText: string
  wer: WERMetrics
  slotAccuracy: SlotAccuracy[]
  domain: string
  audioQuality: 'high' | 'medium' | 'low' | 'very_low'
  speechSNR: number
  processingTimeMs: number
  rejected: boolean
  mismatches: Array<{ expected: string; got: string }>
}

export interface CalibrationPhrase {
  index: number
  text: string
  intent: string
  domain: string
  category: string
  teaching: string
  keyVocab: string[]
  keySlots: Array<{ slotType: string; expected: string }>
}

export interface NoiseBaseline {
  ambientDbEstimate: number
  snrLevel: 'excellent' | 'good' | 'fair' | 'poor'
  recordedAt: string
}

export interface CalibrationStatus {
  hasActive: boolean
  hasCandidate: boolean
  calibratedAt: string | null
  overallWER: number | null
}

export interface CalibrationSaveResult {
  success: boolean
  reason?: string
  promoted: boolean
  comparison: {
    action: 'promote' | 'warn' | 'reject'
    reason: string
    werDelta: number
  }
  profile: {
    overallWER: number
    overallSlotAccuracy: number
    phrasesAccepted: number
    phrasesRejected: number
    qualityGatePassed: boolean
  }
}

// ─────────────────────────────────────────────────────────
// AGC: Hook
// ─────────────────────────────────────────────────────────

export function useVoiceCalibration() {
  const { user, getAuthToken } = useAuth()
  const { language } = useTranslation()
  const backendUrl = getBackendUrl()

  const [state, setState] = useState<CalibrationState>('idle')
  const [phrases, setPhrases] = useState<CalibrationPhrase[]>([])
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0)
  const [phraseResults, setPhraseResults] = useState<CalibrationPhraseResult[]>([])
  const [noiseBaseline, setNoiseBaseline] = useState<NoiseBaseline | null>(null)
  const [saveResult, setSaveResult] = useState<CalibrationSaveResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [calibrationStatus, setCalibrationStatus] = useState<CalibrationStatus | null>(null)

  // AGC: Audio recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)

  // AGC: Fetch calibration status on mount
  useEffect(() => {
    if (user?.uid) {
      fetchCalibrationStatus()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid])

  const fetchCalibrationStatus = useCallback(async () => {
    if (!user?.uid) return
    try {
      const token = await getAuthToken()
      const res = await fetch(`${backendUrl}/api/v1/voice/calibration/status?userId=${user.uid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (res.ok) {
        const data = await res.json()
        setCalibrationStatus(data)
      }
    } catch (err) {
      console.warn('AGC: Failed to fetch calibration status:', err)
    }
  }, [backendUrl, getAuthToken, user?.uid])

  // AGC: Load calibration phrases for user's language
  const loadPhrases = useCallback(async () => {
    setState('loading_phrases')
    setError(null)
    try {
      const lang = (language || 'pt').split('-')[0]
      const res = await fetch(`${backendUrl}/api/v1/voice/calibration/phrases?language=${lang}`)
      if (!res.ok) throw new Error('Failed to load phrases')
      const data = await res.json()
      setPhrases(data.phrases)
      setPhraseResults([])
      setCurrentPhraseIndex(0)
      setSaveResult(null)
      setState('idle')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setError(msg)
      setState('error')
    }
  }, [backendUrl, language])

  // AGC: Request microphone permission and set up audio context
  const setupAudio = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const audioCtx = new AudioContext()
      audioContextRef.current = audioCtx
      const source = audioCtx.createMediaStreamSource(stream)
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 2048
      source.connect(analyser)
      analyserRef.current = analyser

      return true
    } catch (err) {
      console.error('AGC: Microphone permission denied:', err)
      setError('Microphone permission denied')
      setState('error')
      return false
    }
  }, [])

  // AGC: Record noise baseline (5 seconds of silence)
  const recordNoiseBaseline = useCallback(async () => {
    setState('recording_noise')
    setError(null)

    const hasAudio = await setupAudio()
    if (!hasAudio) return

    const analyser = analyserRef.current!
    const dataArray = new Uint8Array(analyser.frequencyBinCount)
    const samples: number[] = []

    // AGC: Sample ambient noise for 5 seconds
    await new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        analyser.getByteFrequencyData(dataArray)
        const avg = dataArray.reduce((sum, v) => sum + v, 0) / dataArray.length
        samples.push(avg)
      }, 100) // AGC: 10 samples/sec

      setTimeout(() => {
        clearInterval(interval)
        resolve()
      }, 5000)
    })

    // AGC: Compute ambient noise estimate
    const avgNoise = samples.length > 0
      ? samples.reduce((s, v) => s + v, 0) / samples.length
      : 0

    // AGC: Map to approximate dB and SNR level
    const dbEstimate = avgNoise > 0 ? 20 * Math.log10(avgNoise / 128) : -60
    let snrLevel: NoiseBaseline['snrLevel']
    if (avgNoise < 5) snrLevel = 'excellent'
    else if (avgNoise < 15) snrLevel = 'good'
    else if (avgNoise < 30) snrLevel = 'fair'
    else snrLevel = 'poor'

    const baseline: NoiseBaseline = {
      ambientDbEstimate: Math.round(dbEstimate * 10) / 10,
      snrLevel,
      recordedAt: new Date().toISOString(),
    }

    setNoiseBaseline(baseline)
    setState('idle')
    return baseline
  }, [setupAudio])

  // AGC: Record a single calibration phrase
  const startPhraseRecording = useCallback(async () => {
    setState('recording_phrase')
    setError(null)
    audioChunksRef.current = []

    // AGC: Ensure audio is set up
    if (!streamRef.current) {
      const hasAudio = await setupAudio()
      if (!hasAudio) return
    }

    const stream = streamRef.current!
    // AGC: Use WebM format (same as VoiceRecordingModal)
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4'

    const recorder = new MediaRecorder(stream, { mimeType })
    mediaRecorderRef.current = recorder

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data)
    }

    recorder.start(250) // AGC: Collect data every 250ms
  }, [setupAudio])

  // AGC: Stop recording and process the phrase
  const stopPhraseRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return

    setState('processing_phrase')

    const recorder = mediaRecorderRef.current
    const phrase = phrases[currentPhraseIndex]
    if (!phrase) {
      setError('No phrase at current index')
      setState('error')
      return
    }

    // AGC: Wait for recorder to stop and collect final data
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType })
        resolve(audioBlob)
      }
      recorder.stop()
    })

    // AGC: Send to backend for processing
    try {
      const token = await getAuthToken()
      const formData = new FormData()
      formData.append('audio', blob, 'calibration.webm')
      formData.append('expectedText', phrase.text)
      formData.append('phraseIndex', String(phrase.index))
      formData.append('language', (language || 'pt').split('-')[0])
      formData.append('domain', phrase.domain)
      formData.append('intent', phrase.intent)
      formData.append('keySlots', JSON.stringify(phrase.keySlots))
      if (user?.uid) formData.append('userId', user.uid)

      const res = await fetch(`${backendUrl}/api/v1/voice/calibration/phrase`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })

      if (!res.ok) throw new Error('Failed to process phrase')
      const data = await res.json()
      const result: CalibrationPhraseResult = data.result

      // AGC: Add result to list
      setPhraseResults(prev => [...prev, result])
      setState('phrase_result')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Processing failed'
      setError(msg)
      setState('error')
    }
  }, [backendUrl, currentPhraseIndex, getAuthToken, language, phrases, user?.uid])

  // AGC: Move to next phrase or complete
  const nextPhrase = useCallback(() => {
    if (currentPhraseIndex < phrases.length - 1) {
      setCurrentPhraseIndex(prev => prev + 1)
      setState('idle')
    } else {
      setState('complete')
    }
  }, [currentPhraseIndex, phrases.length])

  // AGC: Retry current phrase (if quality too low)
  const retryPhrase = useCallback(() => {
    // AGC: Remove the last result (the rejected one)
    setPhraseResults(prev => prev.slice(0, -1))
    setState('idle')
  }, [])

  // AGC: Transcribe-only mode for freeform tests — no WER, no scoring, no phraseResults impact
  // AGC: Uses the regular voice process endpoint to transcribe, returns text only
  const transcribeFreeform = useCallback(async (): Promise<string | null> => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return null

    setState('processing_phrase')

    const recorder = mediaRecorderRef.current
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType })
        resolve(audioBlob)
      }
      recorder.stop()
    })

    try {
      const token = await getAuthToken()
      const formData = new FormData()
      formData.append('audio', blob, 'freeform.webm')
      formData.append('language', (language || 'pt').split('-')[0])
      if (user?.uid) formData.append('userId', user.uid)

      // AGC: Use the voice upload endpoint for real transcription
      const res = await fetch(`${backendUrl}/api/v1/voice/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })

      if (!res.ok) throw new Error('Failed to transcribe')
      const data = await res.json()
      const transcription = data.transcription || data.text || ''
      setState('phrase_result')
      return transcription
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Transcription failed'
      setError(msg)
      setState('error')
      return null
    }
  }, [backendUrl, getAuthToken, language, user?.uid])

  // AGC: Save completed profile
  const saveProfile = useCallback(async () => {
    setState('saving')
    setError(null)

    try {
      const token = await getAuthToken()
      const res = await fetch(`${backendUrl}/api/v1/voice/calibration/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          userId: user?.uid,
          noiseBaseline,
          phraseResults,
          language: (language || 'pt').split('-')[0],
        }),
      })

      if (!res.ok) throw new Error('Failed to save calibration')
      const data: CalibrationSaveResult = await res.json()
      setSaveResult(data)
      setState('saved')

      // AGC: Refresh status after save
      await fetchCalibrationStatus()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Save failed'
      setError(msg)
      setState('error')
    }
  }, [backendUrl, fetchCalibrationStatus, getAuthToken, language, noiseBaseline, phraseResults, user?.uid])

  // AGC: Force promote candidate (user chose to use it despite warning)
  const forcePromote = useCallback(async () => {
    try {
      const token = await getAuthToken()
      const res = await fetch(`${backendUrl}/api/v1/voice/calibration/promote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ userId: user?.uid }),
      })
      if (res.ok) {
        setSaveResult(prev => prev ? { ...prev, promoted: true } : prev)
        await fetchCalibrationStatus()
      }
    } catch (err) {
      console.warn('AGC: Force promote failed:', err)
    }
  }, [backendUrl, fetchCalibrationStatus, getAuthToken, user?.uid])

  // AGC: Cleanup audio resources
  const cleanup = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    analyserRef.current = null
  }, [])

  // AGC: Reset everything
  const reset = useCallback(() => {
    cleanup()
    setState('idle')
    setPhraseResults([])
    setCurrentPhraseIndex(0)
    setNoiseBaseline(null)
    setSaveResult(null)
    setError(null)
  }, [cleanup])

  // AGC: Cleanup on unmount
  useEffect(() => {
    return () => cleanup()
  }, [cleanup])

  // AGC: Computed values
  const currentPhrase = phrases[currentPhraseIndex] || null
  const totalPhrases = phrases.length
  const acceptedCount = phraseResults.filter(r => !r.rejected).length
  const rejectedCount = phraseResults.filter(r => r.rejected).length
  const lastResult = phraseResults[phraseResults.length - 1] || null
  const isRecording = state === 'recording_noise' || state === 'recording_phrase'

  return {
    // AGC: State
    state,
    error,
    phrases,
    currentPhrase,
    currentPhraseIndex,
    totalPhrases,
    phraseResults,
    lastResult,
    noiseBaseline,
    saveResult,
    calibrationStatus,
    acceptedCount,
    rejectedCount,
    isRecording,

    // AGC: Actions
    loadPhrases,
    recordNoiseBaseline,
    startPhraseRecording,
    stopPhraseRecording,
    transcribeFreeform,
    nextPhrase,
    retryPhrase,
    saveProfile,
    forcePromote,
    reset,
    cleanup,
    fetchCalibrationStatus,
  }
}
